import { useLayoutEffect, useRef, useState } from "react";
import { TimeLineStageLabel } from "views/leads-id/slices/Details/slices/ActivityLog/components/TimeLine/TimeLineStageLabel";
import { IdentityVerificationDataEditedItem } from "views/leads-id/slices/Details/slices/ActivityLog/components/TimeLine/IdentityVerificationDataEditedItem";
import { AICallActivityCard } from "views/leads-id/slices/Details/slices/ActivityLog/components/TimeLine/AICallActivityCard";
import {
  ArrowRight02Icon,
  Mail02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { Show } from "shared/components";
import { formatPhone } from "shared/utils";
import { LeadActivity, LeadActivityType } from "views/leads-id/types";
import { LiteTextEditor } from "shared/app/Editor/LiteTextEditor";
import { LinkifiedText } from "shared/utils/linkify-text";
import { EmailPreviewModal } from "shared/components/EmailPreviewModal";
import { describeSmsNotSentReason } from "shared/utils/sms-not-sent-reason";

const MAX_SERIAL_ATTEMPTS = 5;

const ordinal = (n: number): string => {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;

  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const resolveCanonical = (tz: string) =>
  Intl.DateTimeFormat(undefined, { timeZone: tz }).resolvedOptions().timeZone;

const timezoneOptions = (timezone?: string): Intl.DateTimeFormatOptions => {
  if (!timezone) return {};

  const options: Intl.DateTimeFormatOptions = { timeZone: timezone };
  if (resolveCanonical(timezone) !== browserTimezone)
    options.timeZoneName = "short";

  return options;
};

function formatShowingTime(isoString: string, timezone?: string): string {
  return new Date(isoString).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...timezoneOptions(timezone)
  });
}

function formatMoveInDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC"
  });
}

const ShowingInviteSentDescription = ({
  activity
}: {
  activity: LeadActivity;
}) =>
  activity.actor_name ? (
    <p className="text-content-400 text-sm">Sent by: {activity.actor_name}</p>
  ) : null;

function ShowingDescription({ activity }: { activity: LeadActivity }) {
  const location = [
    activity.metadata.property_name,
    activity.metadata.unit_name
  ]
    .filter(Boolean)
    .join(" · ");

  const time = activity.metadata.start_time
    ? formatShowingTime(
        activity.metadata.start_time,
        activity.metadata.timezone
      )
    : "";

  const description = [location, time].filter(Boolean).join(" · ");
  if (!description) return null;

  return <p className="text-content-400 text-sm">{description}</p>;
}

const CHECKED_IN_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true
};

// Guard against malformed timestamps so a bad ISO renders nothing rather than
// "Invalid Date" / "Duration: NaN min".
const toMs = (iso?: string) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
};

const formatWithTz = (iso?: string, timezone?: string) => {
  const ms = toMs(iso);
  return ms === null
    ? null
    : new Date(ms).toLocaleString("en-US", {
        ...CHECKED_IN_TIME_FORMAT,
        ...timezoneOptions(timezone)
      });
};

const formatClockTime = (iso?: string, timezone?: string) => {
  const ms = toMs(iso);
  return ms === null
    ? null
    : new Date(ms).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        ...timezoneOptions(timezone)
      });
};

const ShowingNotCheckedInDescription = ({
  activity
}: {
  activity: LeadActivity;
}) => {
  const endTime = formatClockTime(
    activity.metadata.end_time,
    activity.metadata.timezone
  );

  return (
    <div>
      <ShowingDescription activity={activity} />
      <Show if={!!endTime}>
        <p className="text-content-400 text-sm">
          No check-in by the scheduled end time ({endTime})
        </p>
      </Show>
    </div>
  );
};

const ShowingCheckedInDescription = ({
  activity
}: {
  activity: LeadActivity;
}) => {
  const { lockbox_code, checked_in_at, timezone } = activity.metadata;
  const formattedCheckedInAt = checked_in_at
    ? new Date(checked_in_at).toLocaleString("en-US", {
        ...CHECKED_IN_TIME_FORMAT,
        ...timezoneOptions(timezone)
      })
    : null;

  if (!lockbox_code && !formattedCheckedInAt) return null;

  return (
    <div className="text-sm leading-5 text-content-400">
      {lockbox_code ? <p>Lockbox code: {lockbox_code}</p> : null}
      {formattedCheckedInAt ? (
        <p>Checked in at: {formattedCheckedInAt}</p>
      ) : null}
    </div>
  );
};

const formatCallDuration = (seconds: number | null | undefined) => {
  if (!seconds || seconds <= 0) return null;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")} min`;
};

const formatCallStatus = (status: string | undefined) => {
  if (!status) return null;
  return status.replace(/_/g, " ").replace(/^\w/, (char) => char.toUpperCase());
};

const OutboundCallPlacedDescription = ({
  activity
}: {
  activity: LeadActivity;
}) => {
  const duration = formatCallDuration(activity.metadata.duration_seconds);
  const status = formatCallStatus(activity.metadata.status);

  if (!duration && !status) return null;

  return (
    <div className="text-sm leading-5 text-content-400">
      {status ? (
        <p>
          Status:{" "}
          <span className="text-content-1000 font-medium">{status}</span>
        </p>
      ) : null}
      {duration ? (
        <p>
          Duration:{" "}
          <span className="text-content-1000 font-medium">{duration}</span>
        </p>
      ) : null}
    </div>
  );
};

// Inbound call entry (CRM-905, Figma 23474:91194): From / To / Duration.
// Who answered (or that the call was missed) is carried by the title.
const InboundCallReceivedDescription = ({
  activity
}: {
  activity: LeadActivity;
}) => {
  const { from_number, to_number } = activity.metadata;
  const duration = formatCallDuration(activity.metadata.duration_seconds);

  if (!from_number && !to_number && !duration) return null;

  return (
    <div className="text-sm leading-5 text-content-400">
      {from_number ? (
        <p>
          From:{" "}
          <span className="text-content-1000 font-medium">
            {formatPhone(from_number)}
          </span>
        </p>
      ) : null}
      {to_number ? (
        <p>
          To:{" "}
          <span className="text-content-1000 font-medium">
            {formatPhone(to_number)}
          </span>
        </p>
      ) : null}
      {duration ? (
        <p>
          Duration:{" "}
          <span className="text-content-1000 font-medium">{duration}</span>
        </p>
      ) : null}
    </div>
  );
};

const ShowingCheckedOutDescription = ({
  activity
}: {
  activity: LeadActivity;
}) => {
  const { checked_in_at, checked_out_at, start_time, timezone } =
    activity.metadata;

  const formattedCheckedInAt = formatWithTz(checked_in_at, timezone);
  const formattedCheckedOutAt = formatWithTz(checked_out_at, timezone);

  // duration runs from check-in (or the scheduled start when
  // there's no check-in) to check-out, in whole minutes.
  const checkedOutMs = toMs(checked_out_at);
  const referenceMs = toMs(checked_in_at) ?? toMs(start_time);
  const durationMinutes =
    checkedOutMs !== null && referenceMs !== null
      ? Math.max(0, Math.round((checkedOutMs - referenceMs) / 60000))
      : null;

  if (
    !formattedCheckedInAt &&
    !formattedCheckedOutAt &&
    durationMinutes === null
  )
    return null;

  return (
    <div className="text-sm text-content-400">
      {formattedCheckedInAt ? (
        <p>Checked in at: {formattedCheckedInAt}</p>
      ) : null}
      {formattedCheckedOutAt ? (
        <p>Checked out at: {formattedCheckedOutAt}</p>
      ) : null}
      {durationMinutes === null ? null : <p>Duration: {durationMinutes} min</p>}
    </div>
  );
};

const ShowingFeedbackSubmittedDescription = ({
  activity
}: {
  activity: LeadActivity;
}) => {
  const { start_time, timezone } = activity.metadata;
  const ms = toMs(start_time);
  if (ms === null) return null;

  const tzOptions = timezoneOptions(timezone);
  const dateObj = new Date(ms);
  const date = dateObj.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    ...(timezone ? { timeZone: timezone } : {})
  });
  const time = dateObj.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    ...tzOptions
  });

  return (
    <p className="text-sm text-content-400">
      Showing time: {date} at {time}
    </p>
  );
};

// Clamps a long message preview to 2 lines with a "Show more" toggle, so
// truncation never permanently hides content the user actually wants to see.
const MessagePreview = ({ text }: { text: string }) => {
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    if (!textRef.current) return;

    const checkTruncation = () => {
      const el = textRef.current;
      if (!el || expanded) return;
      setIsTruncated(el.scrollHeight > el.clientHeight);
    };

    checkTruncation();
    const resizeObserver = new ResizeObserver(checkTruncation);
    resizeObserver.observe(textRef.current);
    return () => resizeObserver.disconnect();
  }, [expanded, text]);

  return (
    <div className="text-content-400 text-sm">
      <p ref={textRef} className={expanded ? undefined : "line-clamp-2"}>
        <LinkifiedText text={text} />
      </p>
      <Show if={isTruncated}>
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="text-primary-500 font-medium hover:underline mt-0.5"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      </Show>
    </div>
  );
};

const CAMPAIGN_ACTIVITY_TYPES = new Set<LeadActivityType>([
  LeadActivityType.CAMPAIGN_EMAIL_SENT,
  LeadActivityType.CAMPAIGN_SMS_SENT
]);

// Same label as the campaign's send history: "Step N · day D", and only for
// sequences (one-time and recurring campaigns have a single message).
const campaignStepLabel = ({
  mode,
  position,
  offsetDays
}: {
  mode: unknown;
  position: unknown;
  offsetDays: unknown;
}) => {
  if (typeof position !== "number" || (mode != null && mode !== "drip"))
    return null;
  const step = `Step ${position + 1}`;
  return typeof offsetDays === "number" ? `${step} · day ${offsetDays}` : step;
};

const CampaignSendDescription = ({ activity }: { activity: LeadActivity }) => {
  const {
    communication_campaign_name,
    template_name,
    campaign_mode,
    step_position,
    step_offset_days
  } = activity.metadata;
  const stepLabel = campaignStepLabel({
    mode: campaign_mode,
    position: step_position,
    offsetDays: step_offset_days
  });

  return (
    <div className="text-content-400 text-sm">
      <Show if={Boolean(communication_campaign_name)}>
        <p>
          Campaign:{" "}
          <span className="text-content-1000 font-medium">
            {communication_campaign_name}
          </span>
        </p>
      </Show>
      <Show if={Boolean(template_name)}>
        <p>
          Template:{" "}
          <span className="text-content-1000 font-medium">{template_name}</span>
          {stepLabel ? ` · ${stepLabel}` : null}
        </p>
      </Show>
    </div>
  );
};

const MessageDescription = ({ activity }: { activity: LeadActivity }) => {
  const [open, setOpen] = useState(false);
  const html = activity.metadata.html;

  if (!html)
    return activity.metadata.preview ? (
      <MessagePreview text={activity.metadata.preview} />
    ) : null;

  const via = activity.metadata.source
    ? `Forwarded from ${activity.metadata.source}`
    : activity.title;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-500 hover:underline"
      >
        <HugeiconsIcon icon={Mail02Icon} size={16} />
        View original email
      </button>
      <EmailPreviewModal
        opened={open}
        onClose={() => setOpen(false)}
        subject={activity.metadata.subject}
        via={via}
        html={html}
      />
    </>
  );
};

const SmsNotSentDescription = ({ activity }: { activity: LeadActivity }) => (
  <p className="text-content-400 text-sm">
    {describeSmsNotSentReason(activity.metadata.reason)}
  </p>
);

export const TimeLineDescriptionItem = ({
  activity
}: {
  activity: LeadActivity;
}) => {
  // Kept out of the switch below, which is already at the max-cases limit.
  if (CAMPAIGN_ACTIVITY_TYPES.has(activity.activity_type))
    return <CampaignSendDescription activity={activity} />;
  if (activity.activity_type === LeadActivityType.SHOWING_NOT_CHECKED_IN)
    return <ShowingNotCheckedInDescription activity={activity} />;

  switch (activity.activity_type) {
    case LeadActivityType.STAGE_CHANGED_MANUAL:
    case LeadActivityType.STAGE_CHANGED_AUTO:
      return (
        <div className="flex items-center gap-2 text-sm">
          <TimeLineStageLabel stage={activity.metadata.from_stage} />
          <HugeiconsIcon
            icon={ArrowRight02Icon}
            size={20}
            className="text-content-100"
          />
          <TimeLineStageLabel stage={activity.metadata.to_stage} />
        </div>
      );
    case LeadActivityType.LEAD_CREATED_BY_USER:
    case LeadActivityType.LEAD_CREATED_AUTO: {
      const moveInDate = activity.metadata.desired_move_in_date
        ? formatMoveInDate(activity.metadata.desired_move_in_date)
        : null;

      return (
        <div className="text-content-400 text-sm">
          <p>
            Source:{" "}
            <span className="text-content-1000 font-medium">
              {activity.metadata.source_name}
            </span>
          </p>
          <Show if={moveInDate}>
            <p>
              Desired move-in date:{" "}
              <span className="text-content-1000 font-medium">
                {moveInDate}
              </span>
            </p>
          </Show>
          <Show if={Boolean(activity.metadata.message)}>
            <p>
              Message:{" "}
              <span className="text-content-1000 font-medium break-words whitespace-pre-wrap">
                <LinkifiedText text={activity.metadata.message ?? ""} />
              </span>
            </p>
          </Show>
        </div>
      );
    }

    case LeadActivityType.IDENTITY_VERIFICATION_PASSED:
      return (
        <p className="text-content-400 text-sm">
          Applicant passed identity verification
        </p>
      );
    case LeadActivityType.IDENTITY_VERIFICATION_FAILED:
      return (
        <p className="text-content-400 text-sm">
          Applicant failed identity verification
        </p>
      );
    case LeadActivityType.IDENTITY_VERIFICATION_OVERRIDDEN:
      return (
        <p className="text-content-400 text-sm">
          Overridden by: {activity.metadata.overridden_by}
        </p>
      );
    case LeadActivityType.NOTE_ADDED:
      return (
        <div>
          {activity.rich_text?.document_html ? (
            <LiteTextEditor
              editable={false}
              id="description"
              containerClassName="text-content-400 text-sm"
              initialValue={activity.rich_text.document_html}
            />
          ) : (
            <Show if={Boolean(activity.metadata.note_content)}>
              <p className="text-content-400 text-sm">
                <LinkifiedText text={activity.metadata.note_content ?? ""} />
              </p>
            </Show>
          )}
          <NoteMentionFooter activity={activity} />
        </div>
      );
    case LeadActivityType.LEAD_ASSIGNED: {
      const propertyName = activity.metadata.property_name;
      const unitName = activity.metadata.unit_name;
      if (!propertyName && !unitName) return null;
      return (
        <p className="text-content-400 text-sm">
          {[propertyName, unitName].filter(Boolean).join(" • ")}
        </p>
      );
    }

    case LeadActivityType.FILE_UPLOADED:
    case LeadActivityType.FILE_DELETED:
      return <FileNamesDescription activity={activity} />;

    case LeadActivityType.LEAD_DETAILS_EDITED:
      return <LeadDetailsEditedDescription activity={activity} />;

    case LeadActivityType.IDENTITY_VERIFICATION_DATA_EDITED:
      return <IdentityVerificationDataEditedItem activity={activity} />;

    case LeadActivityType.EMAIL_RECEIVED:
    case LeadActivityType.EMAIL_SENT:
    case LeadActivityType.SMS_RECEIVED:
    case LeadActivityType.SMS_SENT:
    case LeadActivityType.MANUAL_RECORD:
    case LeadActivityType.NEW_INQUIRY:
      return <MessageDescription activity={activity} />;

    case LeadActivityType.SMS_NOT_SENT:
      return <SmsNotSentDescription activity={activity} />;

    case LeadActivityType.PRESCREENING_SUBMITTED:
    case LeadActivityType.PRESCREENING_ABANDONED:
    case LeadActivityType.PRESCREENING_COMPLETED:
      return (
        <p className="text-content-400 text-sm">
          <LinkifiedText text={activity.metadata.message ?? ""} />
        </p>
      );

    case LeadActivityType.SHOWING_INVITE_SENT:
      return <ShowingInviteSentDescription activity={activity} />;

    case LeadActivityType.AI_CALL_RECEIVED:
      return <AICallActivityCard activity={activity} />;

    case LeadActivityType.OUTBOUND_CALL_PLACED:
      return <OutboundCallPlacedDescription activity={activity} />;

    case LeadActivityType.INBOUND_CALL_RECEIVED:
    case LeadActivityType.INBOUND_CALL_FORWARDED:
    case LeadActivityType.INBOUND_CALL_FORWARD_FAILED:
    case LeadActivityType.INBOUND_CALL_TEXT_ONLY:
      return <InboundCallReceivedDescription activity={activity} />;

    case LeadActivityType.SHOWING_FEEDBACK_SUBMITTED:
      return <ShowingFeedbackSubmittedDescription activity={activity} />;

    case LeadActivityType.SHOWING_CHECKED_IN:
      return <ShowingCheckedInDescription activity={activity} />;

    case LeadActivityType.SHOWING_CHECKED_OUT:
      return <ShowingCheckedOutDescription activity={activity} />;

    case LeadActivityType.SHOWING_GUIDED_CONFIRMED:
      return <ShowingGuidedConfirmedDescription activity={activity} />;

    case LeadActivityType.SHOWING_REQUESTED:
    case LeadActivityType.SHOWING_SCHEDULED:
    case LeadActivityType.SHOWING_CONFIRMED:
    case LeadActivityType.SHOWING_COMPLETED:
    case LeadActivityType.SHOWING_NO_SHOW:
    case LeadActivityType.SHOWING_REASSIGNED:
      return <ShowingDescription activity={activity} />;

    case LeadActivityType.SHOWING_SERIAL_NUMBER_INCORRECT:
      return <ShowingSerialIncorrectDescription activity={activity} />;

    case LeadActivityType.SHOWING_SERIAL_NUMBER_LOCKOUT:
      return <ShowingSerialLockoutDescription activity={activity} />;

    case LeadActivityType.SHOWING_RESCHEDULED:
      return <ShowingRescheduledDescription activity={activity} />;

    case LeadActivityType.SHOWING_CANCELLED:
      return <ShowingCancelledDescription activity={activity} />;

    case LeadActivityType.TENANT_NOTIFIED_OF_SHOWING:
      return <TenantNotifiedDescription activity={activity} />;

    case LeadActivityType.PUSH_LEAD_TO_PMS_SUCCESS:
      return activity.metadata.pms_id ? (
        <p className="text-content-400 text-sm">
          Prospect id: {activity.metadata.pms_id}
        </p>
      ) : null;

    case LeadActivityType.PUSH_LEAD_TO_PMS_FAILED:
      return activity.metadata.error_message ? (
        <p className="text-content-400 text-sm">
          Error: {activity.metadata.error_message}
        </p>
      ) : null;

    case LeadActivityType.SHOWING_LOCKBOX_NOT_CONFIGURED:
      return <ShowingLockboxNotConfiguredDescription activity={activity} />;

    default:
      return null;
  }
};

const SHOWING_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true
};

const ShowingRescheduledDescription = ({
  activity
}: {
  activity: LeadActivity;
}) => {
  const { old_start_time, new_start_time, timezone } = activity.metadata;
  if (!old_start_time && !new_start_time) return null;

  const format = (iso?: string) =>
    iso
      ? new Date(iso).toLocaleString("en-US", {
          ...SHOWING_TIME_FORMAT,
          ...timezoneOptions(timezone)
        })
      : "—";

  return (
    <div className="text-sm leading-5 text-content-400">
      {old_start_time ? <p>Original time: {format(old_start_time)}</p> : null}
      {new_start_time ? <p>New time: {format(new_start_time)}</p> : null}
    </div>
  );
};

const TENANT_RECIPIENT_LABELS: Record<string, string> = {
  email: "Email address",
  sms: "Phone"
};

const TENANT_MESSAGE_LABELS: Record<string, string> = {
  email: "Email",
  sms: "Text"
};

const TenantNotifiedDescription = ({
  activity
}: {
  activity: LeadActivity;
}) => {
  const { tenant_name, channel, recipient, message } = activity.metadata;
  const recipientLabel = TENANT_RECIPIENT_LABELS[channel ?? ""] ?? "Contact";
  const messageLabel = TENANT_MESSAGE_LABELS[channel ?? ""] ?? "Message";

  if (!tenant_name && !recipient && !message) return null;

  return (
    <div className="text-sm text-content-400">
      <Show if={Boolean(tenant_name)}>
        <p>Tenant: {tenant_name}</p>
      </Show>
      <Show if={Boolean(recipient)}>
        <p>
          {recipientLabel}: {recipient}
        </p>
      </Show>
      <Show if={Boolean(message)}>
        <p className="text-content-400 whitespace-pre-line">
          {messageLabel}: <LinkifiedText text={message ?? ""} />
        </p>
      </Show>
    </div>
  );
};

const ShowingSerialIncorrectDescription = ({
  activity
}: {
  activity: LeadActivity;
}) => {
  const attempt = activity.metadata.attempt_number;
  if (!attempt) return null;

  return <p className="text-sm text-content-400">{ordinal(attempt)} attempt</p>;
};

const ShowingSerialLockoutDescription = ({
  activity
}: {
  activity: LeadActivity;
}) => {
  const attempts = activity.metadata.attempt_number ?? MAX_SERIAL_ATTEMPTS;

  return (
    <p className="text-sm text-content-400">
      Lead was locked out after {attempts} incorrect serial number attempts
    </p>
  );
};

const ShowingGuidedConfirmedDescription = ({
  activity
}: {
  activity: LeadActivity;
}) => {
  const { checked_in_at, timezone } = activity.metadata;
  if (!checked_in_at) return null;

  const formatted = new Date(checked_in_at).toLocaleString("en-US", {
    ...CHECKED_IN_TIME_FORMAT,
    ...timezoneOptions(timezone)
  });

  return (
    <p className="text-sm leading-5 text-content-400">
      Confirmed at: {formatted}
    </p>
  );
};

const HIDDEN_CHANGE_KEYS = [
  "contact_methods",
  "crm_contact",
  "identity_verification_enabled"
];

type ChangeDisplayValue = string | number | boolean | null;

const formatChangeValue = (value?: ChangeDisplayValue): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const LeadDetailsEditedDescription = ({
  activity
}: {
  activity: LeadActivity;
}) => {
  const changes = activity.metadata.changes;
  if (!changes || typeof changes !== "object") return null;

  const entries = Object.entries(changes)
    .filter(
      ([key, val]) =>
        !HIDDEN_CHANGE_KEYS.includes(key) &&
        typeof val === "object" &&
        !Array.isArray(val) &&
        "label" in val
    )
    .map(([, val]) => val) as Array<{
    label?: string;
    from_display?: ChangeDisplayValue;
    to_display?: ChangeDisplayValue;
  }>;

  if (entries.length === 0) return null;

  return (
    <div className="space-y-1">
      {entries.map((change, i) => (
        <div key={i} className="flex items-center gap-2 text-sm">
          <span className="text-content-400">{change.label || "Field"}:</span>
          <span className="text-content-1000">
            <RenderChangeValue value={change.from_display} />
          </span>
          <HugeiconsIcon
            icon={ArrowRight02Icon}
            size={16}
            className="text-content-100"
          />
          <span className="text-content-1000">
            <RenderChangeValue value={change.to_display} />
          </span>
        </div>
      ))}
    </div>
  );
};

const RenderChangeValue = ({ value }: { value?: ChangeDisplayValue }) => {
  if (typeof value === "string" && value !== "")
    return <LinkifiedText text={value} />;
  return formatChangeValue(value);
};

const FileNamesDescription = ({ activity }: { activity: LeadActivity }) => {
  const names = (activity.metadata.files ?? [])
    .map((file) => file.name)
    .filter(Boolean);
  if (names.length === 0) return null;
  return <p className="text-content-400 text-sm">{names.join(", ")}</p>;
};

const ShowingLockboxNotConfiguredDescription = ({
  activity
}: {
  activity: LeadActivity;
}) => {
  if (!activity.metadata.message) return null;
  return (
    <p className="text-content-400 text-sm">{activity.metadata.message}</p>
  );
};

const ShowingCancelledDescription = ({
  activity
}: {
  activity: LeadActivity;
}) => {
  if (activity.metadata.cancellation_reason !== "prescreening_criteria_decline")
    return null;
  const reasons = activity.metadata.decline_reasons;
  const detail =
    reasons && reasons.length > 0
      ? reasons.join(" and ")
      : "pre-screening criteria";
  const requestedBy = activity.metadata.requested_by;
  return (
    <div className="space-y-1">
      {requestedBy ? (
        <p className="text-content-400 text-sm">Requested by: {requestedBy}</p>
      ) : null}
      <p className="text-content-400 text-sm">Declined due to: {detail}</p>
    </div>
  );
};

const joinNames = (names: string[]) => {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

const NoteMentionFooter = ({ activity }: { activity: LeadActivity }) => {
  const emailedNames = (activity.rich_text?.notifications ?? [])
    .filter(
      (notification) =>
        notification.user != null && notification.metadata?.is_mailed
    )
    .map(
      (notification) =>
        `${notification.user!.name} (${notification.user!.email})`
    );
  if (!emailedNames.length) return null;

  const verb = emailedNames.length > 1 ? "were" : "was";

  return (
    <div className="mt-2 flex gap-2">
      <HugeiconsIcon icon={Mail02Icon} className="text-content-200" size="20" />
      <p className="text-content-400 text-sm">
        {`${joinNames(emailedNames)} ${verb} emailed about the mention`}
      </p>
    </div>
  );
};
